import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Dumbbell } from "lucide-react";
import {
  TRAINING_CATEGORIES,
  type TrainingCategoryKey,
} from "../../../lib/training";
import UserTopNav from "../../../components/layout/UserTopNav";
import { gymConfig } from "../../../config/gymConfig";
import { auth, db } from "../../../firebase";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { prettyDate } from "../utils/movementHelpers";

type DashboardTrainingLog = {
  id: string;
  category: TrainingCategoryKey;
  movementSlug: string;
  movementName: string;
  metricType: string;
  date: string;
};

type CategorySummary = {
  totalLogs: number;
  activeMovements: number;
  latestLog: DashboardTrainingLog | null;
};

function daysSince(dateString?: string | null) {
  if (!dateString) return Number.POSITIVE_INFINITY;

  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return Number.POSITIVE_INFINITY;

  const today = new Date();
  parsed.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const diff = today.getTime() - parsed.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function getCategoryFocusLabel(key: string) {
  if (key === "strength") return "Force";
  if (key === "power") return "Explosive";
  if (key === "engine") return "Conditioning";
  return "Health";
}

export default function Training() {
  const totalMovements = TRAINING_CATEGORIES.reduce(
    (total, category) => total + category.movements.length,
    0
  );

  const [logs, setLogs] = useState<DashboardTrainingLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const user = auth.currentUser;

    if (!user) {
      setLogs([]);
      setLoading(false);
      setLoadError("Sign in to view training progress.");
      return;
    }

    setLoading(true);
    setLoadError("");

    const logsRef = collection(db, "users", user.uid, "trainingLogs");
    const q = query(logsRef, orderBy("date", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const nextLogs: DashboardTrainingLog[] = snapshot.docs.map((doc) => {
          const data = doc.data();

          return {
            id: doc.id,
            category: data.category as TrainingCategoryKey,
            movementSlug: data.movementSlug ?? "",
            movementName: data.movementName ?? "",
            metricType: data.metricType ?? "",
            date: data.date ?? "",
          };
        });

        setLogs(nextLogs);
        setLoading(false);
      },
      (error) => {
        console.error("Error loading training dashboard logs:", error);
        setLogs([]);
        setLoading(false);
        setLoadError(error.message || "Could not load training activity.");
      }
    );

    return () => unsubscribe();
  }, []);

  const categorySummaries = useMemo(() => {
    const summaryMap = new Map<TrainingCategoryKey, CategorySummary>();

    for (const category of TRAINING_CATEGORIES) {
      const categoryLogs = logs.filter((log) => log.category === category.key);
      const activeMovements = new Set(
        categoryLogs.map((log) => log.movementSlug).filter(Boolean)
      ).size;

      summaryMap.set(category.key, {
        totalLogs: categoryLogs.length,
        activeMovements,
        latestLog: categoryLogs[0] ?? null,
      });
    }

    return summaryMap;
  }, [logs]);

  const activeCategories = useMemo(() => {
    return Array.from(categorySummaries.values()).filter(
      (summary) => summary.totalLogs > 0
    ).length;
  }, [categorySummaries]);

  const totalLoggedMovements = useMemo(() => {
    return new Set(logs.map((log) => log.movementSlug).filter(Boolean)).size;
  }, [logs]);

  const latestLog = logs[0] ?? null;

  const mostActiveCategory = useMemo(() => {
    const sorted = [...TRAINING_CATEGORIES].sort((a, b) => {
      const aCount = categorySummaries.get(a.key)?.totalLogs ?? 0;
      const bCount = categorySummaries.get(b.key)?.totalLogs ?? 0;
      return bCount - aCount;
    });

    const top = sorted[0];
    const topCount = top ? categorySummaries.get(top.key)?.totalLogs ?? 0 : 0;

    return top && topCount > 0 ? { label: top.label, count: topCount } : null;
  }, [categorySummaries]);

  const needsAttentionCategory = useMemo(() => {
    const staleCategories = TRAINING_CATEGORIES.map((category) => {
      const summary = categorySummaries.get(category.key);
      const lastDate = summary?.latestLog?.date ?? null;

      return {
        category,
        days: daysSince(lastDate),
        totalLogs: summary?.totalLogs ?? 0,
      };
    }).filter((item) => item.totalLogs > 0);

    staleCategories.sort((a, b) => b.days - a.days);
    return staleCategories[0] ?? null;
  }, [categorySummaries]);

  const readyToRetestCategory = useMemo(() => {
    const candidates = TRAINING_CATEGORIES.map((category) => {
      const summary = categorySummaries.get(category.key);
      const lastDate = summary?.latestLog?.date ?? null;

      return {
        category,
        days: daysSince(lastDate),
        activeMovements: summary?.activeMovements ?? 0,
      };
    }).filter((item) => item.activeMovements >= 2 && item.days >= 14 && item.days < 45);

    candidates.sort((a, b) => b.activeMovements - a.activeMovements || b.days - a.days);
    return candidates[0] ?? null;
  }, [categorySummaries]);

  const momentumCategory = useMemo(() => {
    const active = TRAINING_CATEGORIES.map((category) => {
      const summary = categorySummaries.get(category.key);
      return {
        category,
        totalLogs: summary?.totalLogs ?? 0,
        days: daysSince(summary?.latestLog?.date ?? null),
      };
    }).filter((item) => item.totalLogs > 0 && item.days <= 7);

    active.sort((a, b) => b.totalLogs - a.totalLogs || a.days - b.days);
    return active[0] ?? null;
  }, [categorySummaries]);

  const nextActions = useMemo(() => {
    const actions: Array<{
      title: string;
      detail: string;
      to: string;
      cta: string;
    }> = [];

    if (readyToRetestCategory) {
      actions.push({
        title: `Retest ${readyToRetestCategory.category.label}`,
        detail: `${readyToRetestCategory.activeMovements} active movements and no fresh logs for ${readyToRetestCategory.days} days.`,
        to: `/training/${readyToRetestCategory.category.key}`,
        cta: "Review category",
      });
    }

    if (needsAttentionCategory && needsAttentionCategory.days >= 21) {
      actions.push({
        title: `${needsAttentionCategory.category.label} needs attention`,
        detail: `Last activity was ${needsAttentionCategory.days} days ago. Reopen the category and add a fresh benchmark.`,
        to: `/training/${needsAttentionCategory.category.key}`,
        cta: "Log activity",
      });
    }

    if (momentumCategory) {
      actions.push({
        title: `${momentumCategory.category.label} has momentum`,
        detail: `${momentumCategory.totalLogs} total logs with recent activity in the last week. Good moment to push another benchmark.`,
        to: `/training/${momentumCategory.category.key}`,
        cta: "Keep momentum",
      });
    }

    if (!actions.length) {
      actions.push({
        title: "Start your first benchmark cycle",
        detail: "Pick a category, log a baseline result, then revisit it next week so the dashboard can start coaching you.",
        to: "/training/strength",
        cta: "Start with strength",
      });
    }

    return actions.slice(0, 3);
  }, [momentumCategory, needsAttentionCategory, readyToRetestCategory]);

  return (
    <div className="app-page min-h-screen text-white">
      <UserTopNav />

      <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 py-5 sm:px-6 lg:px-8">
        <section className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(14,14,16,0.98),rgba(8,8,10,0.98))] shadow-[0_22px_60px_rgba(0,0,0,0.34)]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_12%,rgba(79,124,255,0.14),transparent_28%),radial-gradient(circle_at_76%_16%,rgba(121,162,255,0.10),transparent_24%),linear-gradient(180deg,rgba(255,255,255,0.02),transparent_46%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:32px_32px] opacity-[0.04]" />

          <div className="relative p-5 sm:p-6 lg:p-7">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.45fr)_minmax(260px,0.75fr)] lg:items-end">
              <div className="max-w-4xl">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[11px] font-medium text-white/65 backdrop-blur-md">
                  <Dumbbell className="h-3.5 w-3.5" />
                  Training
                </div>

                <h1 className="mt-4 text-3xl font-semibold tracking-[-0.04em] text-white sm:text-4xl lg:text-[2.9rem]">
                  {gymConfig.training.hubTitle}
                </h1>

                <p className="mt-3 max-w-3xl text-sm leading-6 text-white/68 sm:text-[15px]">
                  Explore benchmark categories, log key movements, and keep a live
                  view of where progress is building momentum.
                </p>

                <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-white/52">
                  <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5">
                    {TRAINING_CATEGORIES.length} categories
                  </span>
                  <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5">
                    {totalMovements} total movements
                  </span>
                  <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5">
                    {gymConfig.training.standardTagline}
                  </span>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
                <div className="rounded-[18px] border border-white/10 bg-white/[0.03] p-4 backdrop-blur">
                  <div className="text-[11px] font-medium text-white/45">
                    Active categories
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-white">
                    {activeCategories}
                  </div>
                </div>

                <div className="rounded-[18px] border border-white/10 bg-white/[0.03] p-4 backdrop-blur">
                  <div className="text-[11px] font-medium text-white/45">
                    Logged movements
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-white">
                    {totalLoggedMovements}
                  </div>
                </div>

                <div className="rounded-[18px] border border-white/10 bg-white/[0.03] p-4 backdrop-blur">
                  <div className="text-[11px] font-medium text-white/45">
                    Most active
                  </div>
                  <div className="mt-2 text-sm font-medium text-white/78">
                    {mostActiveCategory
                      ? `${mostActiveCategory.label} · ${mostActiveCategory.count} logs`
                      : "No category activity yet"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-3 md:grid-cols-3">
          <div className="rounded-[18px] border border-white/10 bg-white/[0.025] px-4 py-3.5">
            <div className="text-[11px] font-medium text-white/45">Total logs</div>
            <div className="mt-2 text-2xl font-semibold text-white">{logs.length}</div>
            <div className="mt-2 text-sm text-white/55">
              Entries recorded across all training categories.
            </div>
          </div>

          <div className="rounded-[18px] border border-white/10 bg-white/[0.025] px-4 py-3.5">
            <div className="text-[11px] font-medium text-white/45">Latest activity</div>
            <div className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-white">
              {latestLog ? latestLog.movementName : "—"}
            </div>
            <div className="mt-2 text-sm text-white/55">
              {latestLog
                ? `${latestLog.metricType} · ${prettyDate(latestLog.date)}`
                : "No training activity yet."}
            </div>
          </div>

          <div className="rounded-[18px] border border-white/10 bg-white/[0.025] px-4 py-3.5">
            <div className="text-[11px] font-medium text-white/45">Coverage</div>
            <div className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-white">
              {totalLoggedMovements}/{totalMovements}
            </div>
            <div className="mt-2 text-sm text-white/55">
              Benchmarks you’ve touched so far.
            </div>
          </div>
        </section>

        <section className="grid gap-3 lg:grid-cols-3">
          {nextActions.map((action) => (
            <Link
              key={action.title}
              to={action.to}
              className="group rounded-[20px] border border-white/10 bg-white/[0.025] px-4 py-4 transition hover:border-white/18 hover:bg-white/[0.04]"
            >
              <div className="text-[11px] font-medium text-white/45">Next action</div>
              <div className="mt-2 text-lg font-semibold tracking-[-0.03em] text-white">
                {action.title}
              </div>
              <div className="mt-2 text-sm leading-6 text-white/58">{action.detail}</div>
              <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-white/72 transition group-hover:text-white">
                {action.cta}
                <ChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </div>
            </Link>
          ))}
        </section>

        {loadError ? (
          <div className="rounded-[18px] border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {loadError}
          </div>
        ) : null}

        <section className="grid gap-3 lg:gap-4">
          {TRAINING_CATEGORIES.map((category, index) => {
            const Icon = category.icon;
            const summary = categorySummaries.get(category.key) ?? {
              totalLogs: 0,
              activeMovements: 0,
              latestLog: null,
            };

            return (
              <Link
                key={category.key}
                to={`/training/${category.key}`}
                className="group relative overflow-hidden rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(14,14,16,0.96),rgba(9,9,10,0.96))] transition duration-300 hover:border-white/18 hover:bg-[linear-gradient(180deg,rgba(18,18,22,0.98),rgba(10,10,12,0.98))]"
              >
                <div
                  className={`absolute inset-y-0 left-0 w-px bg-gradient-to-b ${category.accent} opacity-80`}
                />
                <div className="absolute inset-0 opacity-0 transition duration-300 group-hover:opacity-100 bg-[radial-gradient(circle_at_left,rgba(255,255,255,0.05),transparent_32%)]" />

                <div className="relative flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex min-w-0 items-start gap-4 sm:gap-5">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[16px] border border-white/10 bg-white/[0.03] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                      <Icon className="h-5 w-5" />
                    </div>

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="text-xl font-semibold tracking-[-0.03em] text-white sm:text-[1.6rem]">
                          {category.label}
                        </h2>
                        <span className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[10px] font-medium text-white/45">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        {summary.totalLogs > 0 ? (
                          <span className="rounded-full border border-[color:var(--training-accent-border)] bg-[color:var(--training-accent-soft)] px-2.5 py-1 text-[10px] font-medium text-[color:var(--training-accent-text)]">
                            Active
                          </span>
                        ) : null}
                      </div>

                      <p className="mt-2 max-w-2xl text-sm leading-6 text-white/62">
                        {category.description}
                      </p>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[11px] font-medium text-white/48">
                          {category.movements.length} movements
                        </span>
                        <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[11px] font-medium text-white/48">
                          {getCategoryFocusLabel(category.key)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-3 lg:min-w-[360px] lg:grid-cols-[1fr_auto] lg:items-center">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-[16px] border border-white/10 bg-white/[0.03] px-3.5 py-3">
                        <div className="text-[11px] font-medium text-white/45">Progress</div>
                        <div className="mt-2 text-sm font-medium text-white">
                          {summary.activeMovements}/{category.movements.length} movements active
                        </div>
                        <div className="mt-1 text-xs text-white/48">
                          {summary.totalLogs} {summary.totalLogs === 1 ? "log" : "logs"} recorded
                        </div>
                      </div>

                      <div className="rounded-[16px] border border-white/10 bg-white/[0.03] px-3.5 py-3">
                        <div className="text-[11px] font-medium text-white/45">Latest</div>
                        <div className="mt-2 text-sm font-medium text-white">
                          {summary.latestLog ? summary.latestLog.movementName : "No activity yet"}
                        </div>
                        <div className="mt-1 text-xs text-white/48">
                          {summary.latestLog
                            ? `${summary.latestLog.metricType} · ${prettyDate(summary.latestLog.date)}`
                            : "Start logging in this category"}
                        </div>
                      </div>
                    </div>

                    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-medium text-white/55 transition duration-300 group-hover:border-white/18 group-hover:bg-white/[0.06] group-hover:text-white">
                      Open
                      <ChevronRight className="h-4 w-4 transition duration-300 group-hover:translate-x-0.5" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </section>

        {loading ? (
          <div className="rounded-[18px] border border-white/10 bg-white/[0.025] px-4 py-3.5 text-sm text-white/55">
            Loading training dashboard...
          </div>
        ) : null}
      </div>
    </div>
  );
}
